// client/src/pages/ShopPOS/components/CartSidebar.tsx
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ShoppingCart,
  Users,
  Search,
  X,
  Clock,
  Loader2,
  Trash2,
} from "lucide-react";
import { CartItem } from "./CartItem";

interface CartSidebarProps {
  cart: any[];
  cartTotal: number;
  cartTotalWithSurcharge: number;
  displayCartItems: any[];
  products: any[];
  customer: any;
  customerPhone: string;
  customerSearching: boolean;
  inventoryLoading: boolean;
  canUseShifts: boolean;
  currentShift: any;
  isClosingShift: boolean;
  createSaleMutation: any;
  getCurrencySymbol: () => string;
  setCustomerPhone: (phone: string) => void;
  handleCustomerSearch: () => void;
  clearCustomer: () => void;
  onUpdateQuantity: (id: string, delta: number) => void;
  onUpdateSurcharge: (id: string, percentage: number) => void;
  onRemoveFromCart: (id: string) => void;
  onClearCart: () => void;
  onPrepareCloseShift: () => void;
  onCheckout: () => void;
}

export function CartSidebar({
  cart,
  cartTotal,
  cartTotalWithSurcharge,
  displayCartItems,
  products,
  customer,
  customerPhone,
  customerSearching,
  inventoryLoading,
  canUseShifts,
  currentShift,
  isClosingShift,
  createSaleMutation,
  getCurrencySymbol,
  setCustomerPhone,
  handleCustomerSearch,
  clearCustomer,
  onUpdateQuantity,
  onUpdateSurcharge,
  onRemoveFromCart,
  onClearCart,
  onPrepareCloseShift,
  onCheckout,
}: CartSidebarProps) {
  return (
    <>
      {/* Sección de Cliente */}
      <div className="p-4 border-b">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-bold text-sm flex items-center gap-2">
            <Users className="h-4 w-4" />
            Cliente
          </h3>
          {customer && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearCustomer}
              className="h-6 px-2"
            >
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>

        {customer ? (
          <div className="bg-green-50 border border-green-200 rounded p-3">
            <div className="font-bold text-green-800">{customer.fullName}</div>
            <div className="text-xs text-green-600">
              CI: {customer.ci} • Tel: {customer.phone}
            </div>
            <div className="text-xs text-green-600 mt-1">
              Compras anteriores: {customer.totalPurchases || 0} • Total:{" "}
              {getCurrencySymbol()}$
              {Number(customer.totalSpent || 0).toFixed(2)}
            </div>
          </div>
        ) : (
          <div className="flex gap-2">
            <Input
              placeholder="Ej: 54882577 o +5354882577"
              value={customerPhone}
              onChange={(e) => setCustomerPhone(e.target.value)}
              className="text-sm"
              onKeyDown={(e) => e.key === "Enter" && handleCustomerSearch()}
            />
            <Button
              size="icon"
              onClick={handleCustomerSearch}
              disabled={customerSearching}
            >
              {customerSearching ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
            </Button>
          </div>
        )}
      </div>

      {/* Lista de productos del carrito */}
      <div className="flex-1 p-4 overflow-y-auto">
        {cart.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-muted-foreground opacity-50">
            <ShoppingCart className="h-16 w-16 mb-4" />
            <p>El carrito está vacío</p>
            <p className="text-sm">Selecciona productos para comenzar</p>
          </div>
        ) : (
          <div className="space-y-3">
            {cart.map((cartItem) => {
              const item = displayCartItems.find(
                (i: any) => i.id === cartItem.inventoryId,
              );

              if (!item) {
                if (inventoryLoading) {
                  return (
                    <CartItem
                      key={cartItem.inventoryId}
                      cartItem={cartItem}
                      item={{ price: 0 }}
                      product={{ name: "" }}
                      getCurrencySymbol={getCurrencySymbol}
                      onUpdateQuantity={onUpdateQuantity}
                      onUpdateSurcharge={onUpdateSurcharge}
                      onRemove={onRemoveFromCart}
                      isLoading={true}
                    />
                  );
                }
                return (
                  <div
                    key={cartItem.inventoryId}
                    className="bg-red-50 border border-red-200 p-3 rounded-lg"
                  >
                    <div className="text-red-600 text-sm font-medium">
                      Producto no disponible
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 mt-2"
                      onClick={() => onRemoveFromCart(cartItem.inventoryId)}
                    >
                      <Trash2 className="h-3 w-3 mr-1" /> Eliminar
                    </Button>
                  </div>
                );
              }

              const product =
                item.product ||
                products.find((p: any) => p.id === item.productId);

              if (!product) {
                return (
                  <div
                    key={cartItem.inventoryId}
                    className="bg-red-50 border border-red-200 p-3 rounded-lg"
                  >
                    <div className="text-red-600 text-sm font-medium">
                      Producto no disponible
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 mt-2"
                      onClick={() => onRemoveFromCart(cartItem.inventoryId)}
                    >
                      <Trash2 className="h-3 w-3 mr-1" /> Eliminar
                    </Button>
                  </div>
                );
              }

              return (
                <CartItem
                  key={cartItem.inventoryId}
                  cartItem={cartItem}
                  item={item}
                  product={product}
                  getCurrencySymbol={getCurrencySymbol}
                  onUpdateQuantity={onUpdateQuantity}
                  onUpdateSurcharge={onUpdateSurcharge}
                  onRemove={onRemoveFromCart}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Footer con totales y botones */}
      <div className="p-4 border-t bg-muted/10">
        {/* Indicador de turno activo */}
        {canUseShifts ? (
          currentShift && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex justify-between items-center">
                <div>
                  <div className="font-bold text-blue-800">Turno Activo</div>
                  <div className="text-sm text-blue-600">
                    Apertura:{" "}
                    {new Date(currentShift.openedAt).toLocaleTimeString()} |
                    Caja: {currentShift.cashRegisterName || "N/A"}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onPrepareCloseShift}
                  className="text-destructive border-destructive hover:bg-destructive/10"
                  disabled={isClosingShift || !currentShift || currentShift?.closePending}
                >
                  {isClosingShift || currentShift?.closePending ? "Cerrando..." : "Cerrar Turno"}
                </Button>
              </div>
            </div>
          )
        ) : (
          <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-200 rounded-full">
                <Clock className="h-4 w-4 text-gray-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-800">
                  Sistema de Turnos no disponible
                </p>
                <p className="text-xs text-gray-600">
                  La gestión de turnos está disponible en los planes Profesional
                  y Enterprise.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => (window.location.href = "/owner/plans")}
                className="bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100 text-xs"
              >
                Mejorar Plan
              </Button>
            </div>
          </div>
        )}

        {/* Totales */}
        <div className="flex justify-between items-center mb-2">
          <span className="text-muted-foreground">Total original:</span>
          <span className="text-xl font-bold text-muted-foreground">
            {getCurrencySymbol()}
            {cartTotal.toFixed(2)}
          </span>
        </div>
        <div className="flex justify-between items-center mb-6">
          <span className="text-muted-foreground">Total con recargo:</span>
          <span
            className="text-4xl font-heading font-bold"
            data-testid="text-cart-total"
            style={{
              color: cartTotalWithSurcharge > cartTotal ? "#f97316" : "#6b7280",
            }}
          >
            {cartTotalWithSurcharge > cartTotal
              ? `${getCurrencySymbol()}${cartTotalWithSurcharge.toFixed(2)}`
              : `${getCurrencySymbol()}0.00`}
          </span>
        </div>

        {/* Indicador de recargo aplicado */}
        {cartTotalWithSurcharge > cartTotal && (
          <div className="mb-4 p-2 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-center justify-center gap-2">
              <span className="text-amber-600 text-sm">⚡</span>
              <span className="text-amber-700 text-sm font-medium">
                Recargo aplicado a{" "}
                {
                  cart.filter((item) => (item.surchargePercentage || 0) > 0)
                    .length
                }{" "}
                producto(s)
              </span>
            </div>
            <div className="text-xs text-amber-600 text-center mt-1">
              El recargo se aplica solo en pagos con transferencia
            </div>
          </div>
        )}

        {/* Botón vaciar carrito */}
        {cart.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            className="w-full mb-2 text-red-500 border-red-200 hover:bg-red-50 hover:text-red-700"
            onClick={onClearCart}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Vaciar carrito
          </Button>
        )}

        {/* Botón Cobrar */}
        <Button
          size="lg"
          className="w-full text-lg font-bold h-14 uppercase tracking-wide"
          disabled={cart.length === 0 || createSaleMutation.isPending}
          onClick={onCheckout}
          data-testid="button-checkout"
        >
          {createSaleMutation.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Procesando...
            </>
          ) : (
            "Cobrar"
          )}
        </Button>
      </div>
    </>
  );
}
